/**
 * UserTableRow - Table row component for displaying a single user.
 *
 * Displays user information in a table row format.
 */

import { UserTypeBadge, UserEditButton, UserDeleteButton } from '../atoms';
import type { UserResponse } from '@/lib/api';

export interface UserTableRowProps {
  /**
   * User data to display.
   */
  user: UserResponse;
  /**
   * Async callback when edit button is clicked.
   */
  onEdit: (user: UserResponse) => Promise<void>;
  /**
   * Async callback when delete button is clicked.
   */
  onDelete: (user: UserResponse) => Promise<void>;
  /**
   * Function to format date strings.
   */
  formatDate: (dateString: string | null | undefined) => string;
}

export function UserTableRow({ user, onEdit, onDelete, formatDate }: UserTableRowProps) {
  return (
    <tr
      key={user.id}
      className="
        transition-colors
        hover:bg-bg-muted
        dark:hover:bg-bg-subtle
      "
    >
      <td
        className="
          px-6 py-4 text-sm whitespace-nowrap text-text-primary
          dark:text-text-primary
        "
      >
        {user.email}
      </td>
      <td
        className="
          px-6 py-4 text-sm whitespace-nowrap text-text-primary
          dark:text-text-primary
        "
      >
        {user.name || '—'}
      </td>
      <td className="px-6 py-4 text-sm whitespace-nowrap">
        <UserTypeBadge isAdmin={user.is_admin} />
      </td>
      <td
        className="
          px-6 py-4 text-sm whitespace-nowrap text-text-primary
          dark:text-text-primary
        "
      >
        {formatDate(user.expiry_date)}
      </td>
      <td
        className="
          px-6 py-4 text-sm whitespace-nowrap text-text-muted
          dark:text-text-muted
        "
      >
        {formatDate(user.created_at)}
      </td>
      <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          <UserEditButton user={user} onEdit={onEdit} />
          <UserDeleteButton user={user} onDelete={onDelete} />
        </div>
      </td>
    </tr>
  );
}
